import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/feature/ArticleCard';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { getArticleContent } from '@/mocks/articleContent';
import { categories } from '@/mocks/categories';
import { siteConfig } from '@/mocks/siteConfig';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <>
        <SEO title="文章未找到" />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">你访问的文章不存在或已被移除</p>
            <Link to="/" className="px-5 py-2.5 rounded-lg bg-amber-500 text-white font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-amber-400 transition-colors duration-200">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = getRelatedArticles(article.slug, article.category);
  const content = getArticleContent(article.slug);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: category?.name || article.category, to: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: siteConfig.author,
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteConfig.url}/article/${article.slug}`,
    },
  };

  return (
    <>
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={article.tags?.join(',') || '暗网,网络安全'}
        canonicalUrl={`${siteConfig.url}/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
        schema={articleSchema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <article className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            {category && (
              <Link
                to={`/category/${category.slug}`}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-medium mb-4 hover:bg-amber-100 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className={`${category.icon} text-xs`}></i>
                {category.name}
              </Link>
            )}

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200/70">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                阅读时间 {article.readTime}
              </span>
              {article.tags && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 rounded-md bg-background-100 text-foreground-600 text-xs">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
                title={`${article.title} - 51暗网安全指南`}
              />
            </div>

            <div
              className="prose prose-amber prose-lg max-w-none [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:text-foreground-950 [&>h2]:mt-10 [&>h2]:mb-4 [&>p]:text-foreground-700 [&>p]:leading-relaxed [&>p]:mb-4 [&>strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 bg-background-100/60">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <ArticleCard key={related.slug} {...related} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}